/**
 * OpenContactFormButton — universal "open the side support panel" trigger.
 *
 * Replaces public-facing mailto: exposures across the site. Clicking
 * dispatches the `jtr:open-support` window event that StickySupport
 * listens for, sliding open the right-side contact form panel.
 *
 * Why: exposing mailto:support@... in HTML lets crawlers harvest the
 * address and pipe spam. Routing every "Email us" / "Contact us" CTA
 * through the in-page form keeps the inbox clean AND captures useful
 * context (source page, last chat question, user agent) that a raw
 * mailto: never could.
 *
 * Optional props:
 *   prefillSubject / prefillMessage — pre-populate form fields
 *   sourcePage                       — labelling for the support inbox
 *   children                         — replace the default label
 *   as                               — "button" (default) | "a" | "link"
 *   className / variant              — visual styling presets
 */
import { Mail, ArrowRight } from 'lucide-react';

const VARIANTS = {
  primary:  'inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold shadow-md transition-colors',
  outline:  'inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg border-2 border-emerald-600 text-emerald-700 hover:bg-emerald-50 text-sm font-bold transition-colors',
  ghost:    'inline-flex items-center gap-1.5 text-[#62BB46] hover:text-emerald-300 underline-offset-2 hover:underline font-semibold text-sm transition-colors',
  pill:     'inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 hover:bg-emerald-100 text-xs font-bold transition-colors',
  text:     'inline-flex items-center gap-1.5 text-emerald-700 hover:text-emerald-800 underline-offset-2 hover:underline font-semibold',
};

export function openContactForm(detail = {}) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('jtr:open-support', { detail }));
}

export default function OpenContactFormButton({
  variant = 'primary',
  prefillSubject,
  prefillMessage,
  sourcePage,
  className = '',
  showIcon = true,
  showArrow = false,
  children,
  'data-testid': testid = 'open-contact-form-btn',
  ...rest
}) {
  const handleClick = (e) => {
    e.preventDefault();
    openContactForm({
      subject: prefillSubject,
      message: prefillMessage,
      source_page: sourcePage || (typeof window !== 'undefined' ? window.location.pathname : ''),
    });
  };

  const cls = `${VARIANTS[variant] || VARIANTS.primary} ${className}`.trim();

  return (
    <button type="button" onClick={handleClick} className={cls} data-testid={testid} {...rest}>
      {showIcon && <Mail className="w-3.5 h-3.5" strokeWidth={2.4} />}
      <span>{children}</span>
      {showArrow && <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.4} />}
    </button>
  );
}
